
import { ServiceLandingPage } from "@/components/service/ServiceLandingPage";
import { SEO } from "@/components/SEO";
import { Hero } from "@/components/Hero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Contractor } from "@/types/contractor";
import { ContractorsList } from "@/components/ContractorsList";
import { LoadingState } from "@/components/LoadingState";
import { Card, CardContent } from "@/components/ui/card";
import { QuoteRequestForm } from "@/components/services/QuoteRequestForm";

const PestControlPage = () => {
  const serviceType = "Pest Control";
  
  const { data: contractors = [], isLoading } = useQuery({
    queryKey: ['contractors', 'pest-control'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contractors')
        .select('*')
        .eq('service_type', 'Pest Control')
        .eq('location', 'oklahoma-city')
        .order('featured', { ascending: false })
        .order('rating', { ascending: false });

      if (error) {
        console.error('Error fetching contractors:', error);
        throw error;
      }

      return data as Contractor[];
    },
  });

  const features = [
    {
      title: "Pest Identification",
      description: "Expert identification of common Oklahoma pests including rodents, termites, cockroaches, and more"
    },
    {
      title: "Targeted Treatments",
      description: "Customized pest control solutions specific to your infestation type and property needs"
    },
    {
      title: "Preventative Services",
      description: "Ongoing maintenance plans to prevent future pest problems"
    },
    {
      title: "Eco-Friendly Options",
      description: "Safe and environmentally conscious treatment options for homes with children and pets"
    },
    {
      title: "Emergency Response",
      description: "Quick service for urgent pest situations that can't wait"
    }
  ];

  const processSteps = [
    {
      title: "Inspection",
      description: "Thorough property assessment to identify pest issues and entry points"
    },
    {
      title: "Treatment Plan",
      description: "Custom solution designed for your specific pest problem"
    },
    {
      title: "Implementation",
      description: "Professional application of treatments by licensed technicians"
    },
    {
      title: "Follow-Up",
      description: "Scheduled follow-up visits to ensure the problem is resolved"
    }
  ];

  const benefits = [
    {
      title: "Local Expertise",
      description: "Technicians familiar with Oklahoma City's common pest problems"
    },
    {
      title: "Peace of Mind",
      description: "Rest easy knowing your home is protected from unwanted pests"
    },
    {
      title: "Property Protection",
      description: "Prevent costly damage to your home caused by termites and other destructive pests"
    },
    {
      title: "Health Protection",
      description: "Reduce health risks associated with disease-carrying pests"
    },
    {
      title: "Time Savings",
      description: "Professional solutions work faster and more effectively than DIY methods"
    }
  ];

  const breadcrumbItems = [
    { label: "Services", href: "/services" },
    { label: "Pest Control", href: "/pest-control-services-oklahoma-city" },
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Pest Control Services in Oklahoma City",
    "description": "Professional pest control services in Oklahoma City. Effective solutions for all types of pest problems.",
    "provider": {
      "@type": "Organization",
      "name": "Home Pros OKC"
    },
    "areaServed": {
      "@type": "City",
      "name": "Oklahoma City"
    },
    "serviceType": "Pest Control"
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Pest Control Services in Oklahoma City | Home Pros OKC"
        description="Professional pest control services in Oklahoma City. Effective solutions for all types of pest problems."
        canonicalUrl="/pest-control-services-oklahoma-city"
        schema={schema}
      />

      <div className="container mx-auto px-4 py-8">
        <Breadcrumbs items={breadcrumbItems} />
        
        <Hero
          title="Pest Control Services in Oklahoma City"
          subtitle="Protect your home or business from unwanted pests with our network of trusted pest control professionals in Oklahoma City."
          buttonText="Get a Free Quote"
          buttonLink="#quote-form"
        />

        <div className="my-12">
          <h2 className="text-2xl font-bold mb-6">Professional Pest Control You Can Trust</h2>
          <p className="text-lg mb-8">
            Our network of experienced pest control professionals in Oklahoma City can handle any infestation, 
            from common household pests to more serious infestations requiring specialized treatments.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            {features.slice(0, 3).map((feature, index) => (
              <Card key={index}>
                <CardContent className="pt-6">
                  <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                  <p>{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
        
        <div className="my-12">
          <h2 className="text-2xl font-bold mb-6">Our Pest Control Services</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Residential Pest Control</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>General pest control</li>
                  <li>Termite inspection and treatment</li>
                  <li>Rodent control</li>
                  <li>Bed bug elimination</li>
                  <li>Mosquito control</li>
                  <li>Spider and scorpion control</li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Commercial Pest Control</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Office building treatments</li>
                  <li>Restaurant pest control</li>
                  <li>Retail space protection</li>
                  <li>Warehouse pest management</li>
                  <li>Healthcare facility solutions</li>
                  <li>Integrated pest management</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="my-12 p-6 bg-muted rounded-lg">
          <h2 className="text-2xl font-bold mb-4">Benefits of Professional Pest Control</h2>
          <p className="mb-4">
            Here's why hiring professional pest control services makes sense:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            {benefits.map((benefit, index) => (
              <li key={index}>
                <span className="font-semibold">{benefit.title}:</span> {benefit.description}
              </li>
            ))}
          </ul>
        </div>

        <div className="my-12">
          <h2 className="text-2xl font-bold mb-6">Ready to Get Rid of Pests?</h2>
          <p className="text-lg mb-6">
            Connect with top-rated pest control professionals in Oklahoma City today. Fill out our simple form below to receive free, no-obligation quotes.
          </p>
          
          <div id="quote-form">
            <QuoteRequestForm serviceName="Pest Control Services" />
          </div>
        </div>

        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-6">Top Pest Control Professionals in Oklahoma City</h2>
          
          {isLoading ? (
            <LoadingState message="Loading pest control professionals..." />
          ) : contractors && contractors.length > 0 ? (
            <ContractorsList
              contractors={contractors}
              isLoading={isLoading}
              viewMode="grid"
              onViewModeChange={() => {}}
              onServiceTypeChange={() => {}}
              onLocationChange={() => {}}
              selectedServiceType="Pest Control"
            />
          ) : (
            <p className="text-center text-muted-foreground py-8">
              We're currently updating our list of pest control professionals in Oklahoma City. 
              Please check back soon or use the form above to request a quote.
            </p>
          )}
        </div>

        <div className="mt-12 text-center">
          <h3 className="text-xl font-semibold mb-4">Looking for Our Top-Rated Pest Control Services?</h3>
          <p className="mb-6">
            View our curated list of the best pest control services in Oklahoma City.
          </p>
          <a 
            href="/best-pest-control-okc" 
            className="inline-block px-6 py-3 bg-primary text-white rounded-md hover:bg-primary/90 transition"
          >
            See Top-Rated Pest Control Services
          </a>
        </div>
      </div>
    </div>
  );
};

export default PestControlPage;
